import { memo, useCallback, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { WebSocketContext } from './WebSocket'
import { WebSocketRequestMessage, WebSocketRequestMessage_Subscription } from '../../proto/generated/websocket'
import { selectWebsocketSubscribeList } from '../../selectors/websocket'
import { ensureValidAccessToken, subscribeAccessTokenRefresh } from '../../services/auth/tokenRefresh'

type Props = {
    onWebSocketMessageReceived: (data: Uint8Array) => void | Promise<void>
    initialDataFlagRef: React.MutableRefObject<boolean>
}

const AppWebSocketSubscriptionSync = ({ onWebSocketMessageReceived, initialDataFlagRef }: Props) => {
    const ws = useContext(WebSocketContext)
    const subscriptionList = useSelector(selectWebsocketSubscribeList)

    const subscribeSecurities = useCallback(
        async (list: WebSocketRequestMessage_Subscription) => {
            const accessToken = await ensureValidAccessToken()
            if (!accessToken) {
                console.warn('[websocket] skipped subscription sync: no valid access token')
                return
            }

            const hasSubscriptions =
                list.candleSubscriptions.length > 0 ||
                list.securitySnapshotSubscriptions.length > 0 ||
                list.orderBookSubscriptions.length > 0 ||
                list.watchlistSubscriptions.length > 0

            if (!hasSubscriptions) {
                return
            }

            const request = {
                authToken: accessToken,
                subscription: list,
                sendInitialData: initialDataFlagRef.current,
            } as WebSocketRequestMessage

            ws?.sendMessage(WebSocketRequestMessage.encode(request).finish())
        },
        [ws, initialDataFlagRef]
    )

    useEffect(() => {
        if (!ws) return

        ws.addListener(onWebSocketMessageReceived)
        return () => {
            ws.removeListener(onWebSocketMessageReceived)
        }
    }, [ws, onWebSocketMessageReceived])

    useEffect(() => {
        if (!ws) return

        const syncSubscriptions = () => {
            // Avoid queueing a subscribe frame while CONNECTING — onopen will call this again.
            // Prevents duplicate subscribe + duplicate sendInitialData (immediate sync + open listener).
            if (ws.socket?.readyState !== WebSocket.OPEN) {
                return
            }
            void subscribeSecurities(subscriptionList)
        }

        const unsubscribeTokenRefresh = subscribeAccessTokenRefresh(syncSubscriptions)
        ws.addOpenListener(syncSubscriptions)

        // Subscription list changed while already connected — sync once (onopen will not refire).
        if (ws.socket?.readyState === WebSocket.OPEN) {
            syncSubscriptions()
        }

        return () => {
            unsubscribeTokenRefresh()
            ws.removeOpenListener(syncSubscriptions)
        }
    }, [subscribeSecurities, subscriptionList, ws])

    return null
}

export default memo(AppWebSocketSubscriptionSync)
